import ShapeFactory from "@tosize/shapes/src/lib/factory/ShapeFactory";
import type InputType from "@tosize/shapes/src/lib/models/Input/InputType";
import { useShapeInputs } from "@tosize/shapes/src/lib/swr";
import { getMaxBasedOnUnit } from "@tosize/shapes/src/lib/utils";
import { ProductGroup, ProductionUnit } from "@tosize/ui";
import { useCallback, useMemo } from "react";
import { SHAPE_DIMENSIONS_STEP_IDENTIFIER } from "../components/Form/Steps/ShapeDimensions";
import { useCardContext } from "../context/CardContext";
import { useConfig } from "../context/ConfigContext";
import { useErrorContext } from "../context/ErrorContext";
import { usePostDataContext } from "../context/PostDataContext";
import useProducts from "./useProducts";

export default function useShapes() {
	const { data: postData, workpiece } = usePostDataContext();
	const { productData } = useProducts();
	const { apiUrl, productGroup, postcode } = useConfig();
	const { data, error } = useShapeInputs(apiUrl, postData.product, postcode);

	const shapes = useMemo(() => (data ? Object.values(data) : undefined), [data]);

	const shape = useMemo(
		() => shapes?.find((shape) => shape.code === workpiece.shape),
		[workpiece.shape, shapes],
	);

	const shapeModel = useMemo(
		() =>
			shape && productData
				? ShapeFactory.getInstance(shape, postData.unit, productData)
				: undefined,
		[shape, productData, postData.unit],
	);

	const { setWorkpiece } = usePostDataContext();
	const { setErrors, setTouchedFields } = useErrorContext();
	const { scrollToElement } = useCardContext();

	const setCurrentShape = useCallback(
		({ shape, unit, scroll }: { shape: string; unit?: string; scroll?: boolean }) => {
			const currentUnit = (unit as ProductionUnit | undefined) ?? postData.unit;
			const currentShape = shapes?.find((item) => item.code === shape);
			const currentShapeModel = currentShape
				? ShapeFactory.getInstance(currentShape, currentUnit, productData)
				: undefined;

			setErrors((prev) => ({
				...prev,
				shape: [],
				dimensions: {},
				accepted_condition: [],
			}));

			setWorkpiece((prev) => {
				// Create an empty shape dimensions object to reset the form values
				const shape_dimensions = {} as InputType;

				// Map the new shape inputs as empty values to the shape dimensions object
				if (currentShape) {
					for (const input of currentShape.inputs) {
						const isEqualCorners = input.id.startsWith("radius_") || input.id.startsWith("depth_");
						const constraints = currentShapeModel?.getConstraints(
							workpiece.shape_dimensions,
							input,
							isEqualCorners,
						);

						const max =
							constraints !== undefined ? constraints : getMaxBasedOnUnit(input.max, currentUnit);

						if (productData?.checkModeEnabled && productGroup === ProductGroup.Plastic_Sheet) {
							shape_dimensions[input.id] = Math.min(
								1000,
								getMaxBasedOnUnit(input.max, currentUnit),
							);
						} else if (productData?.checkModeEnabled && currentUnit === ProductionUnit.M2) {
							shape_dimensions[input.id] = Math.min(1000, max);
						} else if (productData?.checkModeEnabled) {
							shape_dimensions[input.id] = max;
						} else if (shape_dimensions[input.id] !== undefined) {
							delete shape_dimensions[input.id];
						}
					}
				}

				return { ...prev, shape, shape_dimensions };
			});

			setTouchedFields((prev) => ({ ...prev, dimensions: [] }));

			if (scroll) {
				scrollToElement(`div[data-card="${SHAPE_DIMENSIONS_STEP_IDENTIFIER}"]`);
			}
		},
		[
			postData.unit,
			productData,
			productGroup,
			shapes,
			workpiece.shape_dimensions,
			scrollToElement,
			setTouchedFields,
			setErrors,
			setWorkpiece,
		],
	);

	return {
		shape,
		shapes,
		isLoading: !(data || error) && postData.product,
		shapeModel,
		setCurrentShape,
	} as const;
}
